import React, { useCallback } from 'react';

import { Box } from '@rbilabs/components-library';
import { useNavigate } from 'react-router-dom';

import { ClickableContainer } from 'components/clickable-container';
import SanityBlockRenderer, { SanityBlockContentLinkProps } from 'components/sanity-block-renderer';
import useMediaQuery from 'hooks/use-media-query';
import { CustomEventNames } from 'state/mParticle';

import {
  Background,
  BannerWrapper,
  ButtonWrapper,
  ClickWrap,
  DesktopButtonContainer,
  DesktopLeft,
  DesktopRight,
  GameplayButtonText,
  GameplayButtonWrapper,
  HomepageBannerTypography,
  Image,
  LinkText,
  LinkTriangle,
  MobileButtonContainer,
  MobileLeft,
  MobileRight,
  RulesText,
  StyledLink,
  TopContent,
} from './homepage-banner.styled';
import { IHomepageBannerViewProps } from './types';

const rulesTextSerializers = {
  marks: {
    link: ({ children = [], mark = {} }: SanityBlockContentLinkProps) => {
      const [linkText] = children;
      const { href } = mark;
      return href && linkText ? (
        <StyledLink to={href}>
          <LinkText>{linkText}</LinkText> <LinkTriangle>&#9654;</LinkTriangle>
        </StyledLink>
      ) : null;
    },
  },
};

export const HomepageBannerView: React.FC<IHomepageBannerViewProps> = ({
  cupImage,
  enterButtonLabel,
  enterButtonUrl,
  header,
  rulesText,
  isAuthenticated,
  rollsRemaining,
  rollsRemainingLoading,
}) => {
  const navigate = useNavigate();
  const onClick = useCallback(() => {
    navigate(enterButtonUrl);
  }, [enterButtonUrl, navigate]);

  const isMobile = useMediaQuery('mobile');

  const buttons = (
    <ButtonWrapper>
      {!isAuthenticated ? (
        <GameplayButtonWrapper
          onClick={onClick}
          isRollButton
          to={enterButtonUrl}
          hasRollCount={false}
          eventName={CustomEventNames.RUTR_BUTTON_CLICK_HOME_PAGE_TIME_TO_PLAY}
        >
          {enterButtonLabel}
        </GameplayButtonWrapper>
      ) : (
        <GameplayButtonWrapper
          rollsRemaining={rollsRemaining}
          onClick={onClick}
          isRollButton
          eventName={CustomEventNames.RUTR_BUTTON_CLICK_HOME_PAGE_TIME_TO_PLAY}
          isLoading={rollsRemainingLoading}
          isSmallBtn
        >
          <GameplayButtonText>{enterButtonLabel}</GameplayButtonText>
        </GameplayButtonWrapper>
      )}
    </ButtonWrapper>
  );
  const imageEl = cupImage && <Image image={cupImage} alt="" />;
  const termsText = rulesText && (
    <SanityBlockRenderer
      blockContentOverrides={{
        normal: ({ children }) => <RulesText>{children}</RulesText>,
      }}
      content={rulesText}
      serializers={rulesTextSerializers}
    />
  );

  // Braking this up since the content is structured differently
  let content;
  if (isMobile) {
    content = (
      <Box justify="center" flexDirection="column" height="100%">
        <TopContent onClick={onClick}>
          <MobileLeft>
            <Box flexDirection="column">
              <HomepageBannerTypography centered={false} inverted content={header} />
              <MobileButtonContainer>{buttons}</MobileButtonContainer>
            </Box>
          </MobileLeft>
          <MobileRight>{imageEl}</MobileRight>
        </TopContent>
        {termsText}
      </Box>
    );
    // Desktop view
  } else {
    content = (
      <Box justify="center">
        <Box maxWidth="40rem">
          <DesktopLeft>
            <Box flexDirection="column">
              <ClickableContainer onClick={onClick}>
                <HomepageBannerTypography centered={false} inverted content={header} />
              </ClickableContainer>
              <DesktopButtonContainer>
                {buttons}
                {termsText}
              </DesktopButtonContainer>
            </Box>
          </DesktopLeft>
          <DesktopRight>
            <ClickWrap onClick={onClick}>{imageEl}</ClickWrap>
          </DesktopRight>
        </Box>
      </Box>
    );
  }

  return (
    <BannerWrapper data-testid="HomepageBannerView">
      <Background>{content}</Background>
    </BannerWrapper>
  );
};
